import { Button, useDisclosure } from "@heroui/react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { TableBuilder } from "@/app/components/tables/tableBuilder";
import { FaPencil, FaPlus } from "react-icons/fa6";
import { api } from "@/api";
import { OfficeLocation } from "@/api/locations";
import { CreateLocationModal } from "@/app/components/modals/location/createLocationModal";
import { EditLocationModal } from "@/app/components/modals/location/editLocationModal";
import useErrorHandling from "@/hooks/useErrorHandling";
import { ColumnType } from "@/app/components/tables/tableTypes";
import { useTour } from "@/hooks/useTour";

export function LocationsPage() {
  const [locations, setLocations] = useState<OfficeLocation[] | null>(null);
  const [location, setLocation] = useState<OfficeLocation | null>(null);

  const handleError = useErrorHandling();

  const createLocationModalDisclosure = useDisclosure();
  const editLocationModalDisclosure = useDisclosure();

  useTour(3);

  const fullscreenDisclosure = useDisclosure();

  const editLocation = (location: OfficeLocation) => {
    setLocation(location);
    editLocationModalDisclosure.onOpen();
  };

  const loadLocations = useCallback(() => {
    api.locations
      .list()
      .then(async (response) => {
        setLocations(response.data);
      })
      .catch((err) => {
        const { errorMessage } = handleError(err);
        toast.error(errorMessage);
      });
  }, [handleError]);

  useEffect(() => {
    loadLocations();
  }, [
    createLocationModalDisclosure.isOpen,
    editLocationModalDisclosure.isOpen,
    handleError,
    loadLocations,
  ]);

  return (
    <>
      <CreateLocationModal
        disclosure={createLocationModalDisclosure}
        onLocationCreated={loadLocations}
      />
      <EditLocationModal
        disclosure={editLocationModalDisclosure}
        location={location}
        onLocationUpdated={loadLocations}
      />
      <div className="flex flex-row flex-wrap gap-4 w-full">
        <div className="flex-grow bg-white dark:bg-zinc-900 rounded-xl border-2 border-foreground-200 dark:border-foreground-100 p-4">
          <div className="flex flex-row justify-between w-full items-center mb-4">
            <div className="flex flex-row gap-4 items-center">
              <span className="font-medium" id="locations-title">
                Локации
              </span>
            </div>
            <div className="flex flex-row gap-4 items-center">
              <Button
                startContent={<FaPlus />}
                color="primary"
                variant="solid"
                className="max-w-fit"
                id="create-location-button"
                onClick={() => createLocationModalDisclosure.onOpen()}
              >
                Создать локацию
              </Button>
            </div>
          </div>
          <div className="flex flex-col gap-2 mb-2">
            <TableBuilder
              displayOptions={false}
              removeWrapper={true}
              fullscreenDisclosure={fullscreenDisclosure}
              columns={[
                {
                  key: "display_name",
                  label: "Название",
                  type: ColumnType.String,
                  sortable: true,
                },
                {
                  key: "city_display_name",
                  label: "Город",
                  type: ColumnType.String,
                  sortable: true,
                },
                {
                  key: "address",
                  label: "Адрес",
                  type: ColumnType.String,
                  sortable: true,
                },
                {
                  key: "district",
                  label: "Район",
                  type: ColumnType.String,
                  sortable: true,
                },
                {
                  key: "metro_station",
                  label: "Станция метро",
                  type: ColumnType.String,
                  sortable: true,
                },
                {
                  key: "actions",
                  label: "Действия",
                  type: ColumnType.Actions,
                  actions: [
                    {
                      icon: <FaPencil />,
                      onClick: (_value, row) => {
                        editLocation(row);
                      },
                    },
                  ],
                },
              ]}
              data={locations || []}
              rowsPerPage={10}
              defaultSortDescriptor={{
                column: "display_name",
                direction: "ascending",
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
}
